/**
 * AktionenTab — Configuration AP "actions" tab (§6).
 *
 * Four visible resting states, driven by DB + session state:
 *   - empty           no DB, no active diff session
 *   - analyzing       diff session being opened (AnalyzingProgress)
 *   - staged          diff session awaiting approval (StagedScopesList + Anwenden)
 *   - configured      DB has scopes, no active diff session
 *
 * The upload + chat coordination happens via store signals:
 *   setPendingChatAttachments([file])  → ChatRail uploads to /ap/chat
 *   activeDiffSessionId             ← ChatRail sets after `open_session` action
 *
 * Anwenden enablement in cp7 is analysis-complete. cp9 gates it on the
 * shared "overall accept progress" with the Datenbank diff view.
 */

import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { IconFileText, IconUpload } from "@tabler/icons-react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import clsx from "clsx"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { api } from "../../../api/client"
import { useStore } from "../../../store/useStore"
import type {
  ActionState,
  ConfigurationAnalysis,
  ScopeListItem,
} from "../../../types"
import { StagedScopesList } from "../../assistant/StagedScopesList"
import { AnalyzingProgress } from "../shared/AnalyzingProgress"
import { useFullScopes } from "../shared/useFullScopes"
import { virtuallyOpenAllAPs } from "../shared/virtuallyOpenAPs"

function computeDiffProgress(actionState: ActionState | null): number {
  if (!actionState) return 0
  let total = 0
  let accepted = 0
  for (const sd of Object.values(actionState.diffs)) {
    for (const rec of Object.values(sd.records)) {
      for (const cell of Object.values(rec.cells)) {
        if (cell.secondary) continue
        total += 1
        if (cell.accepted !== null) accepted += 1
      }
    }
  }
  if (total === 0) return 0
  return Math.round((accepted / total) * 100)
}

interface FileMeta {
  name: string
  size: number
}

export function AktionenTab() {
  const { translate } = useTranslation()
  const queryClient = useQueryClient()
  const {
    dbConfigured,
    setDbConfigured,
    scopes,
    activeDiffSessionId,
    setActiveDiffSessionId,
    pendingChatAttachments,
    setPendingChatAttachments,
    setScopes,
    initHiddenScopes,
    setTotalRecordCount,
    setScopeRecordCounts,
    actionState,
    exitDiffMode,
    clearActiveExport,
    clearSelection,
  } = useStore()
  const autoApproveSignal = useStore((s) => s._autoApproveSignal)

  const diffProgress = useMemo(
    () => computeDiffProgress(actionState),
    [actionState],
  )

  const [analyzingFile, setAnalyzingFile] = useState<FileMeta | null>(null)
  const [progressComplete, setProgressComplete] = useState(false)
  const [commitError, setCommitError] = useState<string | null>(null)
  const [isCommitting, setIsCommitting] = useState(false)
  const [draggingOver, setDraggingOver] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const { data: session } = useQuery({
    queryKey: ["session", activeDiffSessionId],
    queryFn: () => api.sessionGet(activeDiffSessionId!),
    enabled: !!activeDiffSessionId,
    staleTime: 5_000,
  })

  const { data: fullScopes } = useFullScopes()

  const analysis =
    session?.analyses && typeof session.analyses === "object"
      ? ((session.analyses as Record<string, unknown>)["_analysis"] as
          | ConfigurationAnalysis
          | undefined)
      : undefined

  // Transition analyzing → staged once the progress animation finishes
  // (AnalyzingProgress only completes when its `dataReady` becomes true,
  // which happens once the session id lands on the store).
  const handleAnalyzingComplete = useCallback(() => {
    setProgressComplete(true)
  }, [])

  // When progress has completed AND the session is known, drop the analyzing
  // overlay. Staged state takes over (driven by activeDiffSessionId).
  useEffect(() => {
    if (progressComplete && activeDiffSessionId) {
      setAnalyzingFile(null)
      setProgressComplete(false)
    }
  }, [progressComplete, activeDiffSessionId])

  // If the upload failed (pendingChatAttachments was consumed but no
  // activeDiffSessionId came back), reset the analyzing overlay once the
  // animation finishes so the user isn't stuck.
  useEffect(() => {
    if (
      progressComplete &&
      !activeDiffSessionId &&
      pendingChatAttachments.length === 0
    ) {
      setAnalyzingFile(null)
      setProgressComplete(false)
    }
  }, [progressComplete, activeDiffSessionId, pendingChatAttachments])

  const startAnalysis = useCallback(
    (file: File) => {
      if (analyzingFile || activeDiffSessionId) return
      setAnalyzingFile({ name: file.name, size: file.size })
      setProgressComplete(false)
      setCommitError(null)
      setPendingChatAttachments([file])
    },
    [analyzingFile, activeDiffSessionId, setPendingChatAttachments],
  )

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) startAnalysis(f)
    if (fileInputRef.current) fileInputRef.current.value = ""
  }

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault()
    setDraggingOver(false)
    const f = e.dataTransfer.files[0]
    if (f) startAnalysis(f)
  }

  const handleAnwenden = useCallback(async () => {
    if (!activeDiffSessionId) return
    setIsCommitting(true)
    setCommitError(null)
    try {
      await api.sessionApprove(activeDiffSessionId)
      // Phase-1 bootstrap of the demo flow: clear leftover monitor /
      // pending-doc state on the BE and seed a fresh Substanz-Radar
      // finding, then virtually-open all notification-bearing APs so the
      // landing page reflects only the radar action the user is meant to
      // see next. Best-effort — a reset-demo failure must not block the
      // commit success path.
      try {
        await api.resetDemo()
      } catch {
        // Non-fatal: env gate may block reset-demo outside dev/demo; the
        // commit itself succeeded so we still finish the flow.
      }
      virtuallyOpenAllAPs()
      // Refresh DB state post-commit.
      const [freshScopes, counts] = await Promise.all([
        api.listScopes().catch(() => [] as ScopeListItem[]),
        api.scopeRecordCounts().catch(() => ({}) as Record<string, number>),
      ])
      setDbConfigured(freshScopes.length > 0)
      setTotalRecordCount(Object.values(counts).reduce((sum, n) => sum + n, 0))
      setScopeRecordCounts(counts)
      if (freshScopes.length > 0) {
        setScopes(freshScopes)
        const hiddenIds = freshScopes
          .filter((s) => s.is_hidden)
          .map((s) => s.id)
        initHiddenScopes(
          freshScopes.map((s) => s.id),
          counts,
          hiddenIds,
        )
      }
      await queryClient.invalidateQueries()
      // A commit replaces every record with new ids; drop any selected/active
      // export record id so downstream views (e.g. the conformity letter) don't
      // query a now-deleted record and render empty.
      clearActiveExport()
      clearSelection()
      setActiveDiffSessionId(null)
      exitDiffMode()
    } catch (err: unknown) {
      const msg =
        err instanceof Error
          ? err.message
          : translate("QMS/CONFIG/IMPORT_FAILED")
      setCommitError(msg)
    } finally {
      setIsCommitting(false)
    }
  }, [
    activeDiffSessionId,
    setActiveDiffSessionId,
    setDbConfigured,
    setScopes,
    initHiddenScopes,
    setTotalRecordCount,
    setScopeRecordCounts,
    queryClient,
    exitDiffMode,
    clearActiveExport,
    clearSelection,
  ])

  const handleVerwerfen = useCallback(async () => {
    if (!activeDiffSessionId) return
    try {
      // Eager-executed session → rollback reverses the events so the
      // DB falls back to its pre-upload state. Reject alone would leave
      // the imported rows behind.
      await api.sessionRollback(activeDiffSessionId)
    } catch {
      // Non-fatal: clear client state so the user can start fresh.
    }
    // Refresh DB state in case rollback succeeded and scopes emptied.
    try {
      const [freshScopes, counts] = await Promise.all([
        api.listScopes().catch(() => [] as ScopeListItem[]),
        api.scopeRecordCounts().catch(() => ({}) as Record<string, number>),
      ])
      setDbConfigured(freshScopes.length > 0)
      setTotalRecordCount(Object.values(counts).reduce((sum, n) => sum + n, 0))
      setScopeRecordCounts(counts)
      if (freshScopes.length > 0) {
        setScopes(freshScopes)
        const hiddenIds = freshScopes
          .filter((s) => s.is_hidden)
          .map((s) => s.id)
        initHiddenScopes(
          freshScopes.map((s) => s.id),
          counts,
          hiddenIds,
        )
      } else {
        setScopes([])
      }
      await queryClient.invalidateQueries()
    } catch {
      // Non-fatal
    }
    // Rollback hard-deletes the imported records; drop stale selected/active
    // export ids so the conformity/inquiry views don't query deleted records.
    clearActiveExport()
    clearSelection()
    setActiveDiffSessionId(null)
    exitDiffMode()
    setAnalyzingFile(null)
    setProgressComplete(false)
  }, [
    activeDiffSessionId,
    setActiveDiffSessionId,
    exitDiffMode,
    setDbConfigured,
    setScopes,
    initHiddenScopes,
    setTotalRecordCount,
    setScopeRecordCounts,
    queryClient,
    clearActiveExport,
    clearSelection,
  ])

  // Auto-approve when ChatRail receives an "accept_all" backend action.
  // This resolves two issues: (1) seedImportDiff may not have finished yet
  // so acceptAllActionScopeDiffs would be a no-op, and (2) the user had to
  // manually click Anwenden after typing "alles richtig".
  useEffect(() => {
    if (autoApproveSignal > 0 && activeDiffSessionId) {
      void handleAnwenden()
    }
    // handleAnwenden omitted: it's a stable useCallback ref and including it
    // would require adding all its inner deps here too.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoApproveSignal, activeDiffSessionId])

  // ---------------------------------------------------------------------
  // Rendering
  // ---------------------------------------------------------------------

  // State: analyzing
  if (analyzingFile) {
    return (
      <div className="flex h-full flex-col bg-grey2 px-6 py-6">
        <FilePreview file={analyzingFile} />
        <div className="mt-6 flex flex-1 items-center justify-center">
          <div className="w-full max-w-md">
            <AnalyzingProgress
              feedbackRound={false}
              dataReady={!!activeDiffSessionId}
              onComplete={handleAnalyzingComplete}
            />
          </div>
        </div>
      </div>
    )
  }

  // State: staged (analysis done, awaiting Anwenden)
  if (activeDiffSessionId) {
    const file = session?.files?.[0]
    return (
      <div className="flex h-full flex-col bg-grey2">
        <div className="shrink-0 border-b border-grey4 px-6 py-4">
          {file && (
            <FilePreview
              file={{ name: file.name, size: file.size }}
              rows={analysis?.rows ?? 0}
            />
          )}
          {analysis?.summary && (
            <p className="mt-3 text-sm text-white">{analysis.summary}</p>
          )}
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-grey5">
            {translate("QMS/CONFIG/SUGGESTED_CHANGES")}
          </h4>
          {analysis ? (
            <StagedScopesList analysis={analysis} />
          ) : (
            <p className="py-6 text-center text-sm text-grey5">
              {translate("QMS/CONFIG/ANALYSIS_LOADING")}
            </p>
          )}
        </div>

        {commitError && (
          <div className="mx-6 mb-3 rounded border border-red1/30 bg-red1/10 p-2 text-xs text-red1">
            {commitError}
          </div>
        )}

        <div className="shrink-0 border-t border-grey4 bg-grey2 px-6 py-3">
          <div className="mb-2 flex items-center gap-2">
            <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-grey4">
              <div
                className="h-full bg-yellow1 transition-[width] duration-200 ease-out"
                style={{ width: `${diffProgress}%` }}
              />
            </div>
            <span className="shrink-0 text-[11px] text-grey5">
              {Math.round(diffProgress)}%
            </span>
          </div>
          {diffProgress < 100 && (
            <p className="mb-2 text-center text-[11px] text-grey5">
              {translate("QMS/CONFIG/ACCEPT_ALL_HELP")}
            </p>
          )}
          <div className="flex items-center justify-center gap-3">
            <button
              onClick={handleVerwerfen}
              disabled={isCommitting}
              className="rounded-lg border border-grey4 px-4 py-2 text-sm text-grey6 hover:bg-grey3 disabled:opacity-40"
            >
              {translate("QMS/CONFIG/DISCARD")}
            </button>
            <button
              onClick={handleAnwenden}
              disabled={!analysis || isCommitting || diffProgress < 100}
              className="rounded-lg bg-yellow1 px-6 py-2 text-sm font-medium text-black hover:bg-yellow5/60 disabled:cursor-not-allowed disabled:opacity-40"
              title={
                diffProgress < 100
                  ? translate("QMS/CONFIG/APPLY_DISABLED_TITLE")
                  : undefined
              }
            >
              {isCommitting
                ? translate("QMS/CONFIG/APPLYING")
                : translate("QMS/CONFIG/APPLY")}
            </button>
          </div>
        </div>
      </div>
    )
  }

  // State: configured or empty — both show upload affordance + live scopes list
  return (
    <div className="flex h-full flex-col bg-grey2">
      <div className="flex-1 overflow-y-auto px-6 py-6">
        {/* Status / upload section */}
        {dbConfigured ? (
          <div className="rounded-lg border border-grey4 bg-grey3 px-4 py-3">
            <p className="text-sm font-medium text-white">
              {translate("QMS/CONFIG/SCOPES_CONFIGURED", {
                count: scopes.length,
              })}
            </p>
            <p className="mt-0.5 text-xs text-grey5">
              {translate("QMS/CONFIG/OPEN_DATABASE_HELP")}
            </p>
          </div>
        ) : (
          <div className="rounded-lg border border-grey4 bg-grey3 px-4 py-3">
            <p className="text-sm font-medium text-white">
              {translate("QMS/CONFIG/DB_NOT_CONFIGURED")}
            </p>
            <p className="mt-0.5 text-xs text-grey5">
              {translate("QMS/CONFIG/UPLOAD_OR_CHAT_HELP")}
            </p>
          </div>
        )}

        <div
          onDrop={handleDrop}
          onDragEnter={(e) => {
            e.preventDefault()
            setDraggingOver(true)
          }}
          onDragOver={(e) => {
            e.preventDefault()
            if (!draggingOver) setDraggingOver(true)
          }}
          onDragLeave={(e) => {
            if (e.currentTarget.contains(e.relatedTarget as Node | null)) return
            setDraggingOver(false)
          }}
          onClick={() => fileInputRef.current?.click()}
          className={clsx(
            "mt-4 cursor-pointer rounded-xl border-2 border-dashed text-center transition-colors",
            dbConfigured ? "px-4 py-5" : "px-4 py-8",
            draggingOver
              ? "border-yellow5 bg-yellow5/10"
              : "border-grey4 hover:border-yellow5 hover:bg-yellow5/10",
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileInput}
            className="hidden"
          />
          <IconUpload
            size={dbConfigured ? 22 : 28}
            className={clsx(
              "mx-auto text-grey5",
              dbConfigured ? "mb-2" : "mb-3",
            )}
          />
          <p className="text-sm font-medium text-white">
            {dbConfigured
              ? translate("QMS/CONFIG/IMPORT_ANOTHER")
              : translate("QMS/CONFIG/DROP_DOCUMENT")}
          </p>
          <p className="mt-1 text-xs text-grey5">
            {translate("QMS/CONFIG/CSV_OR_EXCEL")}
          </p>
        </div>

        {/* Scopes list — always visible */}
        <div className="mt-6">
          <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-grey5">
            {translate("QMS/CONFIG/CONFIGURATION")}
          </h4>
          <StagedScopesList liveScopes={fullScopes ?? []} />
        </div>
      </div>
    </div>
  )
}

function FilePreview({ file, rows }: { file: FileMeta; rows?: number }) {
  const { translate } = useTranslation()
  return (
    <div className="flex items-center gap-2 text-xs text-grey5">
      <IconFileText size={14} />
      <span className="font-mono text-white">{file.name}</span>
      <span>·</span>
      <span>{(file.size / 1024).toFixed(0)} KB</span>
      {rows != null && rows > 0 && (
        <>
          <span>·</span>
          <span>
            {rows.toLocaleString("de-DE")} {translate("QMS/CONFIG/ROWS_LABEL")}
          </span>
        </>
      )}
    </div>
  )
}
